import { useMemo } from "react";

export interface UseTimeRangeStatusParams {
  hasStart: boolean;
  hasEnd: boolean;
  isStartLate: boolean;
  isEndLate: boolean;
}

export interface TimeRangeStatusClasses {
  wrapperClass: string;
  startClass: string;
  endClass: string;
}

/**
 * Hook que determina quais classes CSS aplicar baseado nos cenários de atraso
 * 
 * Cenários:
 * 1. Ambos existem e ambos estão atrasados → pinta apenas wrapper
 * 2. Ambos existem, apenas start atrasado → pinta start
 * 3. Ambos existem, apenas end atrasado → pinta end
 * 4. Tem start, não tem end, start atrasado → pinta start
 * 5. Tem start, não tem end, end atrasado (fim previsto passou) → pinta end
 * 6. Não tem start, tem end, end atrasado → pinta end
 * 7. Não tem start nem end, mas end atrasado (AWAITING_END oneshot) → pinta end
 * 8. Nenhum atrasado ou nenhum existe → não pinta nada
 */
export function useTimeRangeStatus(
  params: UseTimeRangeStatusParams,
  styles: Record<string, string>
): TimeRangeStatusClasses {
  const { hasStart, hasEnd, isStartLate, isEndLate } = params;

  return useMemo(() => {
    const both = hasStart && hasEnd;

    // Caso 1: Ambos existem e ambos estão atrasados → pinta apenas wrapper
    if (both && isStartLate && isEndLate) {
      return {
        wrapperClass: styles.late || "",
        startClass: "",
        endClass: "",
      };
    }

    // Caso 2: Ambos existem, apenas start atrasado → pinta start
    if (both && isStartLate && !isEndLate) {
      return {
        wrapperClass: "",
        startClass: styles.late || "",
        endClass: "",
      };
    }

    // Caso 3: Ambos existem, apenas end atrasado → pinta end
    if (both && !isStartLate && isEndLate) {
      return {
        wrapperClass: "",
        startClass: "",
        endClass: styles.late || "",
      };
    }

    // Caso 4: Tem start, não tem end, start atrasado → pinta start
    if (hasStart && !hasEnd && isStartLate) {
      return {
        wrapperClass: "",
        startClass: styles.late || "",
        endClass: "",
      };
    }

    // Caso 5: Tem start, não tem end, end atrasado (fim previsto passou) → pinta end
    if (hasStart && !hasEnd && !isStartLate && isEndLate) {
      return {
        wrapperClass: "",
        startClass: "",
        endClass: styles.late || "",
      };
    }

    // Caso 6: Não tem start, tem end, end atrasado → pinta end
    if (!hasStart && hasEnd && isEndLate) {
      return {
        wrapperClass: "",
        startClass: "",
        endClass: styles.late || "",
      };
    }

    // Caso 7: Não tem start nem end, mas end atrasado (AWAITING_END oneshot) → pinta end
    if (!hasStart && !hasEnd && isEndLate) {
      return {
        wrapperClass: "",
        startClass: "",
        endClass: styles.late || "",
      };
    }

    // Caso 8: Nenhum atrasado ou nenhum existe → não pinta nada
    return {
      wrapperClass: "",
      startClass: "",
      endClass: "",
    };
  }, [hasStart, hasEnd, isStartLate, isEndLate, styles]);
}
